import React, { useState, useEffect, useRef } from 'react';

const NowPlaying = () => {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  
  // Use refs instead of state for timers to prevent re-renders
  const progressTimerRef = useRef(null);
  const pollTimerRef = useRef(null);

  // Format time in MM:SS
  const formatTime = (milliseconds) => {
    if (!milliseconds) return '0:00';
    
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  // Fetch currently playing track
  const fetchNowPlaying = async () => {
    try {
      const response = await fetch('/.netlify/functions/now-playing');
      
      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
        return;
      }
      
      if (data.message) {
        // No track playing
        setCurrentTrack(null);
        return;
      }
      
      // Update track information
      setCurrentTrack(data);
      
      // Update progress and duration
      if (data.progress_ms !== undefined && data.duration_ms) {
        setProgress(data.progress_ms);
        setDuration(data.duration_ms);
      }
    } catch (err) {
      console.error('Error fetching now playing:', err);
      setError('Failed to fetch currently playing track');
    } finally {
      setIsLoading(false);
    }
  };

  // Setup progress updater
  useEffect(() => {
    // Clear existing timer
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
    
    // Only set timer if we have a track that's playing
    if (currentTrack?.isPlaying) {
      progressTimerRef.current = setInterval(() => {
        setProgress(prevProgress => {
          // Don't increment beyond track duration
          if (prevProgress >= duration) {
            return prevProgress;
          }
          return prevProgress + 1000;
        });
      }, 1000);
    }
    
    // Cleanup on unmount
    return () => {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
      }
    };
  }, [currentTrack, duration]);

  // Setup polling
  useEffect(() => {
    // Initial fetch
    fetchNowPlaying();
    
    // Set up regular polling (every 30 seconds)
    pollTimerRef.current = setInterval(fetchNowPlaying, 30000);
    
    // Cleanup on unmount
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }
    };
  }, []);

  if (isLoading && !currentTrack) {
    return (
      <div style={{ padding: '20px', color: '#666', fontFamily: 'Tahoma, Arial, sans-serif' }}>
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', color: '#666', fontFamily: 'Tahoma, Arial, sans-serif' }}>
        {error}
      </div>
    );
  }

  if (!currentTrack) {
    return (
      <div style={{ padding: '20px', color: '#666', fontFamily: 'Tahoma, Arial, sans-serif' }}>
        Nothing currently playing
      </div>
    );
  }

  // Calculate progress percentage
  const progressPercent = duration > 0 ? (progress / duration) * 100 : 0;

  return (
    <div style={{ 
      padding: '20px',
      fontFamily: 'Tahoma, Arial, sans-serif',
      width: '300px'
    }}>
      <div style={{ 
        display: 'flex',
        alignItems: 'center',
        marginBottom: '15px'
      }}>
        {currentTrack.albumArt && (
          <img 
            src={currentTrack.albumArt} 
            alt="Album Art"
            style={{
              width: '64px',
              height: '64px',
              marginRight: '15px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
          />
        )}
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
            {currentTrack.name}
          </div>
          <div style={{ color: '#666' }}>
            {currentTrack.artist}
          </div>
          <div style={{ color: '#666', fontSize: '0.9em' }}>
            {currentTrack.album}
          </div>
        </div>
      </div>

      {/* Windows XP style progress bar */}
      <div style={{ marginBottom: '8px' }}>
        <div style={{
          background: '#D4D0C8', // Windows XP gray
          border: '1px solid #808080',
          borderRadius: '1px',
          height: '10px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            background: 'linear-gradient(to right, #0078D7, #0063B1)', // Windows XP blue gradient
            height: '100%',
            width: `${progressPercent}%`
          }} />
        </div>

        {/* Time indicators */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '11px',
          color: '#666',
          marginTop: '2px'
        }}>
          <span>{formatTime(progress)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      <div style={{ 
        color: currentTrack.isPlaying ? '#22C55E' : '#888',
        fontSize: '0.8em',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center'
      }}>
        <span style={{ marginRight: '5px' }}>
          {currentTrack.isPlaying ? '▶' : '❙❙'}
        </span>
        {currentTrack.isPlaying ? 'PLAYING' : 'PAUSED'}
      </div>
    </div>
  );
};

export default NowPlaying;